import React, { useCallback, useMemo } from 'react';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloud, faDownload } from '@fortawesome/free-solid-svg-icons';
import i18n from 'i18n';
import ToggleButtons from 'components/common/ToggleButtons/ToggleButtons';
import { CustomField } from 'components/common/form/field/CustomField';
import { SelectField } from 'components/common/form/field/SelectField';
import Select from 'components/common/Select/Select';
import { DateRangePicker } from 'components/common/DateRangePicker/DateRangePicker';
import { StickableTanstackTable } from 'components/common/Table/StickableTanstackTable';
import {
  TableChangeState,
  TableChangeType
} from 'components/common/Table/types';
import { getWeatherVerificationColumns } from './weatherVerificationColumns';
import {
  KEY_COLUMN,
  MAX_QUERY_DAYS,
  SIZE_PER_PAGE,
  WeatherVerificationTabModelData,
  WeatherVerificationTableRecord
} from './WeatherVerificationTabModel';
import styles from './weatherVerificationTab.module.scss';

type WeatherVerificationProps = {
  model: WeatherVerificationTabModelData;
  /** 訂單走期，限制日期選擇器可選的範圍 */
  minDate: string;
  maxDate: string;
};

export const WeatherVerificationFilters: React.FC<WeatherVerificationProps> = ({
  model,
  minDate,
  maxDate
}) => {
  const {
    from,
    to,
    result,
    campaignId,
    storeNums,
    resultFilterOptions,
    campaignOptions,
    storeOptions,
    dayRangeError,
    canQuery,
    onDateRangeChange,
    onResultChange,
    onCampaignChange,
    onStoreChange,
    onQueryBtnClick
  } = model;

  return (
    <>
      <CustomField
        label={i18n.t('weatherVerification.labels.timeSlot')}
        name='weatherVerificationTimeSlot'
      >
        <div className={styles.timeSlotFilter}>
          <ToggleButtons
            name='weatherVerificationResult'
            value={result}
            options={resultFilterOptions}
            onChange={onResultChange}
          />
          <div className={styles.dateRangeArea}>
            <DateRangePicker
              minDate={minDate}
              maxDate={maxDate}
              startDate={from}
              endDate={to}
              format='YYYY-MM-DD'
              showTimePicker={false}
              onChange={onDateRangeChange}
              popOverClassName={styles.dateRangePicker}
            />
            {dayRangeError ? (
              <span className={styles.errorHint}>{dayRangeError}</span>
            ) : (
              <span className={styles.hint}>
                {i18n.t('weatherVerification.labels.maxQueryDaysHint', {
                  days: MAX_QUERY_DAYS
                })}
              </span>
            )}
          </div>
        </div>
      </CustomField>
      <SelectField
        label={i18n.t('weatherVerification.labels.campaign')}
        name='weatherVerificationCampaign'
        className={styles.campaignSelect}
        options={campaignOptions}
        value={campaignId}
        simpleValue
        required
        placeholder={i18n.t('common.placeholder.pleaseSelect')}
        onChange={onCampaignChange}
      />
      <CustomField
        label={i18n.t('weatherVerification.labels.store')}
        name='weatherVerificationStore'
      >
        <Select
          className={styles.storeSelect}
          name='weatherVerificationStore'
          options={storeOptions}
          value={storeNums}
          isMulti
          simpleValue
          isClearable
          maxMenuHeight={200}
          placeholder={i18n.t('common.placeholder.pleaseSelect')}
          onChange={onStoreChange}
        />
      </CustomField>
      <CustomField label='' name='weatherVerificationQuery'>
        <Button
          variant='primary'
          size='sm'
          onClick={onQueryBtnClick}
          disabled={!canQuery}
        >
          {i18n.t('report.buttons.search')}
        </Button>
      </CustomField>
    </>
  );
};

export const WeatherVerificationResult: React.FC<
  Pick<WeatherVerificationProps, 'model'>
> = ({ model }) => {
  const {
    campaign,
    campaignConditionSummary,
    campaignOptions,
    campaignsLoaded,
    tableData,
    totalSize,
    page,
    onPageChange,
    onExportCsv,
    canExportCsv
  } = model;

  const columns = useMemo(() => getWeatherVerificationColumns(), []);

  const handleTableChange = useCallback(
    (
      type: TableChangeType,
      newState: TableChangeState<WeatherVerificationTableRecord>
    ) => {
      if (type === 'pagination') {
        onPageChange(newState.page);
      }
    },
    [onPageChange]
  );

  // 群組內沒有任何有資料的天氣鎖定活動 —— 結果區整段換成空狀態。
  if (campaignsLoaded && campaignOptions.length === 0) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyStateIcon}>
          <FontAwesomeIcon icon={faCloud} />
        </div>
        <div className={styles.emptyStateTitle}>
          {i18n.t('weatherVerification.descriptions.emptyTitle')}
        </div>
        <div className={styles.emptyStateHint}>
          {i18n.t('weatherVerification.descriptions.emptyHint')}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={styles.exportRow}>
        <button
          type='button'
          className={styles.exportButton}
          onClick={onExportCsv}
          disabled={!canExportCsv}
        >
          <FontAwesomeIcon icon={faDownload} />
          {i18n.t('weatherVerification.labels.exportCsv')}
        </button>
      </div>
      {campaign && (
        <div className={styles.campaignHeader}>
          <span className={styles.campaignName}>{campaign.campaignName}</span>
          <span className={styles.campaignConditions}>
            {campaignConditionSummary}
          </span>
        </div>
      )}
      <StickableTanstackTable
        remote
        keyField={KEY_COLUMN}
        data={tableData}
        columns={columns}
        onTableChange={handleTableChange}
        noDataIndication={i18n.t('common.labels.noData')}
        pagination={{
          page,
          sizePerPage: SIZE_PER_PAGE,
          totalSize
        }}
      />
    </>
  );
};
